// TODO: review moving to document reducer scope
import * as type from '../constants/chart-action-types';
import { THEMES } from '../components/chartview/panels/colors/colors-panel/colors-panel.constants';
import { getDefaultGroups, getSizesForTarget, getTargets } from '../constants/targets';
import { findColors, removeCategoryColumn } from './colors';
import { copyObject } from './copyObject';
import importData from './importData';
import { getOffsets } from './indexOffsets';
import invalidate from './invalidate';
import mustRender from './mustRender';
import possibleCharts from './possibleCharts';
import sortDataByDate from './sortDataByDate';
import updateDirty from './updateDirty';
import updateParsedData from './updateParsedData';

/**
 * Data.
 */
function receiveInputData(chartDoc, action) {
  return updateInputData(chartDoc, { prop: 'raw', value: action.data });
}

function updateInputData(chartDoc, action) {
  chartDoc = chartDoc.setIn(['chart', 'data', action.prop], action.value);
  chartDoc = invalidate(chartDoc, '*', 'chart/data');
  chartDoc = invalidate(chartDoc, '*', 'chart/annotations');
  chartDoc = sortDataByDate(chartDoc);
  return updateDirty(chartDoc);
}

function updateDataField(chartDoc, action) {
  chartDoc = chartDoc.setIn(['chart', 'data', action.field], action.value);
  return updateDirty(chartDoc);
}

function listCompanies(chartDoc, action) {
  chartDoc = chartDoc.setIn(['chart', 'data', 'companies'], action.value);
  return updateDirty(chartDoc);
}
function updateMarketdataProp(chartDoc, action) {
  const { prop, values, prefix, suffix, showAsPercentProps } = action;
  switch (prop) {
    case 'tickers':
      chartDoc = updateTickers(chartDoc, values, prefix, suffix, showAsPercentProps);
      chartDoc = updateDates(chartDoc, values);
      break;
    case 'dateRange':
    case 'frequency':
    case 'update':
      chartDoc = updateDates(chartDoc, values);
      break;
    case 'isPercent':
      chartDoc = showAsPercent(chartDoc, values.isPercent, prefix, suffix);
      break;
  }
  return updateDirty(chartDoc);
}

function updateDates(chartDoc, values) {
  for (const prop in values) {
    chartDoc = chartDoc.setIn(['chart', 'data', prop], values[prop]);
  }
  return chartDoc;
}

function updateTickers(chartDoc, values, prefix, suffix, showAsPercentProps) {
  const { chart } = chartDoc;
  const { data, sizes } = chart;
  const { tickers, isPercent } = data;
  const updatedTickers = values.tickers;
  const oldLength = tickers.length;
  const newLength = updatedTickers.length;
  const sizeKeys = Object.keys(sizes);
  chartDoc = chartDoc.setIn(['chart', 'data', 'tickers'], updatedTickers);

  if (oldLength === 0) {
    chartDoc = addFirstTicker(chartDoc, prefix, suffix);
  } else if (newLength === 0) {
    // when all instruments removed, hide chart div (chart still created because chart engine can't currently handle 0 series)
    chartDoc = setChartsVisibility(chartDoc, { bool: false });
  }
  if (newLength === 1) {
    chartDoc = updateForSingleTicker(
      chartDoc,
      sizeKeys,
      oldLength,
      isPercent,
      updatedTickers[0],
      prefix,
      suffix
    );
  }
  if (showAsPercentProps) {
    chartDoc = updateShowAsPercent(chartDoc, showAsPercentProps, newLength, prefix, suffix);
  }
  chartDoc = invalidate(chartDoc, '*', 'chart/data');
  return chartDoc;
}
/**
 * Updates prefix/suffix if percentage change set to true for ex.: removes prefix and set suffix to be '%'
 * Updates disableShowAsPercent property which is a main control for "Show as percentage change" in the Data Panel.
 * @param {{setIn: () => void}} chartDoc
 * @param {{disabled: boolean, isPercent: boolean}} showAsPercentProps
 * @param {*} newLength
 * @param {string} prefix
 * @param {string} suffix
 * @returns updated version of chartDoc
 */
function updateShowAsPercent(chartDoc, showAsPercentProps, newLength, prefix, suffix) {
  const { disabled, isPercent } = showAsPercentProps;
  if (isPercent) chartDoc = showAsPercent(chartDoc, isPercent, prefix, suffix);
  // @ts-ignore
  return chartDoc.setIn(['chart', 'data', 'disableShowAsPercent'], disabled);
}

function addFirstTicker(chartDoc, prefix, suffix) {
  const { chart } = chartDoc;
  const { data, showCharts } = chart;
  if (!showCharts) {
    // if div is hidden, make it visible
    chartDoc = setChartsVisibility(chartDoc, { bool: true });
  }
  // set defaults for market data charts
  chartDoc = chartDoc.setIn(['chart', 'data', 'connectEmptyDataPoints'], 'dotted');
  return chartDoc;
}

function updateForSingleTicker(
  chartDoc,
  sizeKeys,
  oldLength,
  isPercent,
  firstTicker,
  prefix,
  suffix
) {
  const { company, type } = firstTicker;
  if (oldLength === 2) {
    chartDoc = marketDataYAxisUnit(chartDoc, { suffix });
  }
  if (isPercent || oldLength === 0) {
    chartDoc = marketDataYAxisUnit(chartDoc, { prefix, suffix });
    chartDoc = updateYAxis(chartDoc, type, firstTicker);
  }
  return chartDoc;
}

function checkCents(prefix) {
  return prefix === '¢';
}

function updateYAxis(chartDoc, type, firstTicker) {
  switch (type) {
    case 'Future':
    case 'Stock':
      return marketDataYAxisUnit(chartDoc, { commas: true });
    case 'Index':
      return marketDataYAxisUnit(chartDoc, { commas: false });
    case 'Currency':
    case 'CryptoCurrency':
      const prefix = getPrefix(firstTicker.ticker.slice(-3));
      return marketDataYAxisUnit(chartDoc, { commas: true, prefix });
    case 'Bond':
      return marketDataYAxisUnit(chartDoc, { commas: true });
    default:
      return chartDoc;
  }
}

function getPrefix(currency) {
  switch (currency) {
    case 'USD':
      return '$';
    default:
      return '';
  }
}

function setChartsVisibility(chartDoc, action) {
  return chartDoc.setIn(['chart', 'showCharts'], action.bool);
}

function marketDataYAxisUnit(chartDoc, values) {
  for (const prop in values) {
    chartDoc = updateUnits(chartDoc, { target: '*', prop, value: values[prop] }, true, true);
  }
  return chartDoc;
}

function showAsPercent(chartDoc, isPercent, prefixValue, suffixValue) {
  chartDoc = chartDoc.setIn(['chart', 'data', 'isPercent'], isPercent);
  const [prefix, suffix] = isPercent ? ['', '%'] : [prefixValue, suffixValue];
  chartDoc = marketDataYAxisUnit(chartDoc, { prefix, suffix });
  return chartDoc;
}

function getSuffix(isCents, needsPercent, value) {
  if (needsPercent) return '%';
  if (isCents) return value;
  return '';
}

function toggleRolling(chartDoc, action) {
  chartDoc = chartDoc.setIn(['chart', 'data', 'isRolling'], action.bool);
  return updateDirty(chartDoc);
}

function setSingleIsPercent(chartDoc, action) {
  chartDoc = chartDoc.setIn(['chart', 'data', 'singleIsPercent'], action.bool);
  return updateDirty(chartDoc);
}

function updateSeriesName(chartDoc, action) {
  // Change series names back in the original raw data. It must match against
  // the original/previous name to ensure we are changing a column header and
  // not a piece of data, in case the data does not have a first row that is
  // treated as series names.
  const origName = chartDoc.chart.data.series[action.index].name;
  let { raw } = chartDoc.chart.data;
  const lines = raw.split(/[\n|\r]/);
  for (let i = 0; i < lines.length; i++) {
    if (!/^\s*$/.test(lines[i])) {
      const cols = lines[i].split(/\t/);
      if (cols[action.index] === origName) {
        cols[action.index] = action.name;
        lines[i] = cols.join('\t');
        raw = '';
        for (let j = i; j < lines.length; j++) {
          raw += `${lines[j]}\n`;
        }
        chartDoc = chartDoc.setIn(['chart', 'data', 'raw'], raw);
      }
      break;
    }
  }
  // Change series names in the chart data object:
  chartDoc = chartDoc.setIn(['chart', 'data', 'series', action.index, 'name'], action.name);
  chartDoc = invalidate(chartDoc, '*', 'chart/series-names');
  chartDoc = invalidate(chartDoc, '*', 'chart/annotations');
  return updateDirty(chartDoc);
}

function getY(data) {
  // eslint-disable-next-line no-plusplus
  for (let i = data.length - 2; i >= 0; i--) {
    if (data[i].y !== null || data[i].y !== undefined) return data[i].y;
  }
}

function updateSeriesOrder(chartDoc, action) {
  const series = chartDoc.chart.data.series.asMutable({ deep: true });
  if (action.itemsInOrder) {
    series.forEach((series, i) => {
      series.initialIndex = i;
    });
    series.sort((a, b) => {
      let [aY, bY] = [a.data[a.data.length - 1].y, b.data[b.data.length - 1].y];
      if (!aY) aY = getY(a.data);
      if (!bY) bY = getY(b.data);
      return bY - aY;
    });
  } else
    series.sort((a, b) => {
      if (b.initialIndex !== undefined && a.initialIndex !== undefined) {
        if (typeof b.data[a.data.length - 1].y === 'string') {
          return b;
        }
        return a.initialIndex - b.initialIndex;
      }
    });

  chartDoc = chartDoc.setIn(['chart', 'data', 'series'], series);

  chartDoc = invalidate(chartDoc, '*', 'chart/series-names');
  return updateDirty(chartDoc);
}

function updateSeriesMarkdown(chartDoc, action) {
  chartDoc = chartDoc.setIn(['chart', 'data', 'series', action.index, 'markdown'], action.text);
  chartDoc = invalidate(chartDoc, '*', 'chart/series-names');
  return updateDirty(chartDoc);
}

function hideSeriesLegend(chartDoc, action) {
  chartDoc = chartDoc.setIn(['chart', 'data', 'series', action.index, 'showInLegend'], action.bool);
  chartDoc = invalidate(chartDoc, '*', 'chart/hide-legend');
  return updateDirty(chartDoc);
}

function updateDateTimeSuppression(chartDoc, action) {
  chartDoc = chartDoc.setIn(['chart', 'data', 'suppress', action.prop], action.value);
  chartDoc = invalidate(chartDoc, '*', 'chart/data');
  return updateDirty(chartDoc);
}

function updateTargets(chartDoc, action) {
  chartDoc = chartDoc.setIn(['chart', 'targets'], [action.target]);
  chartDoc = chartDoc.setIn(['chart', 'groups'], getDefaultGroups(action.target));
  chartDoc = chartDoc.setIn(['chart', 'product'], action.target);
  chartDoc = invalidate(chartDoc, '*', 'chart/targets');
  chartDoc = chartDoc.setIn(['mustRender'], mustRender(chartDoc));
  const value = action.target === 'wsj' ? 'wsj_world' : action.target;
  return updateColorProperty(chartDoc, { prop: 'theme', type: type.UPDATE_COLOR, value });
}

function updateGroups(chartDoc, action) {
  const groups = chartDoc.chart.groups.asMutable();
  const index = groups.indexOf(action.group);
  if (index === -1) {
    if (action.group === 'Video') {
      chartDoc = updateColorTheme(chartDoc, { value: 'wsj_video' });
      chartDoc = chartDoc.setIn(['status'], 'video');
      chartDoc = chartDoc.setIn(['chart', 'data', 'showTooltips'], false);
      groups.forEach((group) => {
        if (group !== 'Preview') {
          chartDoc = chartDoc.setIn(['mustRender'], mustRender(chartDoc, { removeGroup: group }));
        }
      });
      groups.splice(0, groups.length, 'Video', 'Preview');
    } else {
      groups.push(action.group);
    }
    chartDoc = chartDoc.setIn(['mustRender'], mustRender(chartDoc, { addGroup: action.group }));
  } else {
    if (action.group === 'Video') {
      chartDoc = chartDoc.setIn(['status'], chartDoc.urls.published ? 'published' : 'draft');
      chartDoc = chartDoc.setIn(['chart', 'data', 'showTooltips'], true);
      groups.splice(0, groups.length, 'Article', 'Article (wide)', 'Preview');
      ['Article', 'Article (wide)'].forEach((group) => {
        chartDoc = chartDoc.setIn(['mustRender'], mustRender(chartDoc, { addGroup: group }));
      });
    } else {
      groups.splice(index, 1);
    }
    chartDoc = chartDoc.setIn(['mustRender'], mustRender(chartDoc, { removeGroup: action.group }));
  }
  chartDoc = chartDoc.setIn(['chart', 'groups'], groups);
  return updateDirty(chartDoc);
}

function updatePrintInfo(chartDoc, action) {
  chartDoc = chartDoc.setIn(['chart', 'print', action.prop], action.value);
  return updateDirty(chartDoc);
}

function updateSections(chartDoc, action) {
  const valueIndex = chartDoc.chart.admin.sections.indexOf(action.section);
  const sections = chartDoc.chart.admin.sections.toMutable();
  if (valueIndex === -1) {
    sections.push(action.section);
  } else {
    sections.splice(valueIndex, 1);
  }
  section.sort();
  chartDoc = chartDoc.setIn(['chart', 'admin', 'sections'], sections);
  return updateDirty(chartDoc);
}

/**
 * Updates.
 */

function updateType(chartDoc, type) {
  return chartDoc.setIn(['chart', 'type'], type);
}

function updateChartType(chartDoc, action) {
  const { chartType: type, needsUpdate } = action;

  const prevChartType = chartDoc.chart?.type;
  if (prevChartType === 'pie') updateColorTheme(chartDoc, { value: null });

  const needColorPointReset =
    ['column', 'bar'].includes(chartDoc.chart.type) &&
    !['column', 'bar'].includes(action.chartType);
  if (needColorPointReset) {
    const copySeries = copyObject(chartDoc.chart.data.series);
    copySeries.forEach((s) => {
      if (s.color) {
        s.data.forEach((point) => {
          if (point.color) delete point.color;
        });
      }
    });
    chartDoc = chartDoc.setIn(['chart', 'data', 'series'], copySeries);
  }
  // set step prop to true for step charts
  chartDoc = chartDoc.setIn(['chart', 'step'], type === 'step');
  // if switching from bar/column to other type, ensure color mode is default
  if (type !== 'bar' || type !== 'column') {
    chartDoc = chartDoc.setIn(['chart', 'color', 'mode'], 'DEFAULT');
  }

  const isScatter = type === 'scatter';
  if (isScatter || chartDoc.chart.data.isScatterplot) {
    const objForUpdateInputData = {
      value: isScatter,
      prop: 'isScatterplot',
    };
    chartDoc = updateInputData(chartDoc, objForUpdateInputData);
    const data = importData(type, chartDoc.chart.data.parsed, chartDoc.chart.data);
    chartDoc = updateParsedData(chartDoc, { data });
  }
  const dataType = chartDoc.chart.data.type;
  if (dataType === 'market' && needsUpdate !== false) {
    const { parsed, series, categories } = chartDoc.chart.data;

    if (type === 'column') {
      chartDoc = marketDataYAxisUnit(chartDoc, { suffix: '%', prefix: '' });
    } else if (type === 'line' && chartDoc.chart.type === 'column') {
      const { prefix, suffix } = parsed[0];
      chartDoc = marketDataYAxisUnit(chartDoc, { suffix, prefix });
    }
    const names =
      type === 'bar' ? series.map(({ name, symbol }) => ({ name, symbol })) : categories;

    const data = importData('market', parsed, { chartType: type, names });

    // for single-instrument bar chart converted to line, update selector to line and column
    const charts =
      chartDoc.chart.type === 'bar' && series[0].data.length === 1
        ? possibleCharts(chartDoc.chart.data)
        : null;
    chartDoc = updateParsedData(chartDoc, { data, charts });
  }

  chartDoc = updateType(chartDoc, type);
  chartDoc = invalidate(chartDoc, '*', 'chart/type');
  chartDoc = chartDoc.setIn(['mustRender'], mustRender(chartDoc));
  if (type === 'area') {
    return updateStacking(chartDoc, { value: 'normal' });
  }

  if (type === 'pie') {
    const defaultPieTheme = THEMES[chartDoc.chart.product][type][0];
    chartDoc = updateColorTheme(chartDoc, { value: defaultPieTheme });
  }
  return updateDirty(chartDoc);
}

function updateAxesProp(chartDoc, prop, value) {
  const sizes = copyLiveSizes(chartDoc);
  const keys = Object.keys(sizes);
  for (let i = 0; i < keys.length; i++) {
    const size = sizes[keys[i]];
    size.axes[prop] = value;
    chartDoc = invalidate(chartDoc, keys[i], 'chart/axis');
  }
  chartDoc = chartDoc.merge({ chart: { sizes } }, { deep: true });
  return updateDirty(chartDoc);
}

function updateUnits(chartDoc, action, doAxes, doTips) {
  const keys = Object.keys(chartDoc.chart.sizes);
  keys.forEach((key) => {
    if (doAxes) {
      chartDoc = chartDoc.setIn(
        ['chart', 'sizes', key, 'axes', 'units', action.prop],
        action.value
      );
    }
    if (doTips) {
      chartDoc = chartDoc.setIn(['chart', 'sizes', key, 'tooltips', action.prop], action.value);
    }
    chartDoc = invalidate(chartDoc, key, 'chart/axis');
    chartDoc = invalidate(chartDoc, key, 'chart/annotations');
  });
  return chartDoc;
}

// Updates display for axis data and tooltips.
function updateNumericDataDisplay(chartDoc, action) {
  // Update tooltips and axis units in a single UI component, but keep their
  // separate data models in sync here:
  const doAxes = action.target === 'axes' || action.target === '*';
  const doTips = action.target === 'tooltips' || action.target === '*';
  chartDoc = updateUnits(chartDoc, action, doAxes, doTips);
  if (action.prop === 'sameAsAxes' && action.value) {
    const keys = Object.keys(chartDoc.chart.sizes);
    keys.forEach((key) => {
      const props = Object.keys(chartDoc.chart.sizes[key].axes.units);
      props.forEach((prop) => {
        const tips = chartDoc.chart.sizes[key].tooltips;
        if (tips.hasOwnProperty(prop)) {
          chartDoc = chartDoc.setIn(
            ['chart', 'sizes', key, 'tooltips', prop],
            chartDoc.chart.sizes[key].axes.units[prop]
          );
        }
      });
    });
  }
  // Set additional defaults if the divisor is changed:
  if (action.prop === 'divisor' && (doAxes || doTips)) {
    chartDoc = updateDivisorDefaults(chartDoc, action.value, doAxes, doTips);
  }

  return updateDirty(chartDoc);
}

// When the divisor changes, other defaults for numerical display need to
// update also, such as suffix ("millions") and decimals. This function
// should always be called from updateNumericDataDisplay() and never directly
// as the result of an action.
function updateDivisorDefaults(chartDoc, divisor, doAxes, doTips) {
  const keys = Object.keys(chartDoc.chart.sizes);
  const divisorProps =
    divisor === 'none' || !divisor
      ? {
          commas: false,
          prefix: '',
          suffix: '',
          formatDecimals: false,
          decimals: 2,
          divisor,
        }
      : {
          commas: true,
          suffix: ` ${divisor.slice(0, -1)}`,
          formatDecimals: false,
          decimals: 0,
          divisor,
        };
  keys.forEach((key) => {
    if (doAxes) {
      chartDoc = chartDoc.setIn(['chart', 'sizes', key, 'axes', 'units'], divisorProps);
    }
    if (doTips) {
      divisorProps.sameAsAxes = chartDoc.chart.sizes[key].tooltips.sameAsAxes;
      chartDoc = chartDoc.setIn(['chart', 'sizes', key, 'tooltips'], divisorProps);
    }
    chartDoc = invalidate(chartDoc, key, 'chart/axis');
  });
  return chartDoc;
}

function enableMarkdown(chartDoc, action) {
  chartDoc = chartDoc.setIn(['chart', 'markdown'], action.enabled);
  return updateDirty(chartDoc);
}

function updateHorizontalBarType(chartDoc, action) {
  const horizontalBarType =
    action.value === 'normal' || action.value === 'aboveBar' || action.value === 'noScaleAndLeft'
      ? action.value
      : null;
  chartDoc = chartDoc.setIn(['chart', 'data', 'horizontalBarType'], horizontalBarType);
  chartDoc =
    action.value !== 'normal' ? chartDoc.setIn(['chart', 'data', 'showTooltips'], false) : chartDoc;
  chartDoc = invalidate(chartDoc, '*', 'chart/data');
  return updateDirty(chartDoc);
}

function updateStacking(chartDoc, action) {
  const keys = Object.keys(chartDoc.chart.sizes);
  const stacking = action.value === 'normal' || action.value === 'percent' ? action.value : null;
  for (let i = 0; i < keys.length; i++) {
    chartDoc = chartDoc.setIn(['chart', 'sizes', keys[i], 'stacking'], stacking);
    chartDoc = invalidate(chartDoc, keys[i], 'chart/stacking');
  }
  if (stacking === 'percent') {
    chartDoc = updateNumericDataDisplay(chartDoc, {
      prop: 'prefix',
      target: '*',
      value: '',
    });
    return updateNumericDataDisplay(chartDoc, {
      prop: 'suffix',
      target: '*',
      value: '%',
    });
  }
  return updateDirty(chartDoc);
}

function updateChartSizeProp(chartDoc, action) {
  chartDoc = chartDoc.setIn(['chart', 'sizes', action.size, action.prop], action.value);
  if (action.render) {
    chartDoc = invalidate(chartDoc, action.size, 'chart/height');
  }
  if (action.render && action.prop === 'width') {
    chartDoc = invalidate(chartDoc, action.size, 'chart/width');
  }
  return updateDirty(chartDoc);
}

function setWidthHeightManually(chartDoc, action) {
  chartDoc = chartDoc.setIn(['chart', action.prop], action.enabled);
  return updateDirty(chartDoc);
}

function updateGlobalChartHeight(chartDoc, action) {
  const TARGETS = getTargets();

  TARGETS.forEach((target) => {
    const targetSizes = getSizesForTarget(target.value);
    const { sizes } = chartDoc.chart;
    targetSizes.forEach((targetSize) => {
      const minH = targetSize.minHeight;
      const maxH = targetSize.maxHeight;
      if (minH !== undefined && maxH !== undefined) {
        const { id } = targetSize;
        const newH = minH + Math.round((maxH - minH) * (action.value / 100));
        chartDoc = chartDoc.setIn(['chart', 'sizes', id, 'height'], newH);
        if (action.render) {
          chartDoc = invalidate(chartDoc, id, 'chart/height');
        }
      }
    });
  });
  return updateDirty(chartDoc);
}

// A data point on a chart has been selected; normalize/adjust for
// firstColumnAsCategories and firstRowAsNames settings.
// TODO: re-implement!
function selectChartPoint(chartDoc, point) {
  const { data } = chartDoc.chart;
  const { seriesOffset, categoryOffset } = getOffsets(data);
  return chartDoc.set(
    'selectedPoint',
    point
      ? {
          series: point.series + seriesOffset,
          point: point.point + categoryOffset,
        }
      : null
  );
}

function selectAdjustedChartPoint(chartDoc, action) {
  return chartDoc.set('selectedPoint', {
    series: action.series,
    point: action.point,
  });
}

function updateColors(chartDoc, action) {
  const { seriesOffset, categoryOffset } = getOffsets(chartDoc.chart.data);
  if (action.type === type.UPDATE_DATA_COLOR) {
    // set data point color to undefined if same as series color (or negativeColor for negative values)
    const thresholdChart = chartDoc.chart.color.mode === 'THRESHOLD';
    const { y } = chartDoc.chart.data.series[action.series].data[action.point];
    const colorProp = thresholdChart && y < 0 ? 'negativeColor' : 'color';
    const color =
      action.color === chartDoc.chart.data.series[action.series][colorProp]
        ? undefined
        : action.color;
    chartDoc = chartDoc.setIn(
      ['chart', 'data', 'series', action.series, 'data', action.point, 'color'],
      color
    );
    chartDoc = invalidate(
      chartDoc,
      '*',
      `chart/colors/${action.series - seriesOffset}/${action.point - categoryOffset}/${
        action.color
      }`
    );
  } else if (action.type === type.UPDATE_SERIES_COLOR) {
    chartDoc = chartDoc.setIn(
      ['chart', 'data', 'series', action.series, action.prop],
      action.color
    );
    const seriesIndex = action.series - seriesOffset;
    chartDoc = invalidate(chartDoc, '*', `chart/colors/${seriesIndex}/${action.color}`);
    chartDoc = invalidate(chartDoc, '*', `chart/colors/${seriesIndex}/*/${action.color}`);
  } else if (action.type === type.UPDATE_SERIES_LINE) {
    chartDoc = chartDoc.setIn(['chart', 'data', 'series', action.series, 'line'], action.line);
    const seriesIndex = action.series - seriesOffset;
    chartDoc = invalidate(chartDoc, '*', `chart/colors/${seriesIndex}/${action.line}`);
  }
  return updateDirty(chartDoc);
}

function updateColorProperty(chartDoc, action) {
  switch (action.prop) {
    case 'theme':
      return updateColorTheme(chartDoc, action);
    case 'mode':
    case 'threshold':
    case 'opacity':
      chartDoc = chartDoc.setIn(['chart', 'color', action.prop], action.value);
      chartDoc = invalidate(chartDoc, '*', `chart/colors/${action.prop}`);
      break;
    case 'reset':
      chartDoc.chart.data.series.forEach((item, index) => {
        chartDoc = resetColors(chartDoc, { index, color: action.color });
      });
      break;
  }
  return updateDirty(chartDoc);
}

function resetColors(chartDoc, action) {
  // for old charts befor negativeColor property was implemented
  const currentSeries = chartDoc.chart.data.series[action.index];
  const color = currentSeries.thresholdAbove || currentSeries.color;
  const negativeColor =
    currentSeries.negativeColor || currentSeries.thresholdBelow || currentSeries.color;
  chartDoc = chartDoc.setIn(['chart', 'data', 'series', action.index, 'color'], color);
  chartDoc = chartDoc.setIn(
    ['chart', 'data', 'series', action.index, 'negativeColor'],
    negativeColor
  );
  chartDoc = chartDoc.setIn(['chart', 'data', 'series', action.index, 'thresholdAbove'], undefined);
  chartDoc = chartDoc.setIn(['chart', 'data', 'series', action.index, 'thresholdBelow'], undefined);
  const { data } = currentSeries;
  for (let i = 0; i < data.length; i++) {
    if (currentSeries.data[i].color === color || chartDoc.chart.color.mode === 'THRESHOLD') {
      chartDoc = chartDoc.setIn(
        ['chart', 'data', 'series', action.index, 'data', i, 'color'],
        action.color
      );
    }
  }
  return chartDoc;
}

function updateColorTheme(chartDoc, action) {
  chartDoc = updateSeriesColorTheme(action.value, chartDoc);
  return updateDirty(chartDoc);
}

function updateSeriesColorTheme(themeName, chartDoc) {
  const { data, product } = chartDoc.chart;
  const [series, offset] = removeCategoryColumn(data);
  chartDoc = chartDoc.setIn(['chart', 'color', 'theme'], themeName);
  const getColorProps = findColors(series, offset, product, themeName);
  series.forEach((currentSeries, index) => {
    const colorProps = getColorProps(currentSeries, index);
    const { seriesIdx } = colorProps;
    ['color', 'negativeColor'].forEach((prop) => {
      const color = colorProps[prop];
      chartDoc = chartDoc.setIn(['chart', 'data', 'series', seriesIdx, prop], color);
      chartDoc = invalidate(chartDoc, '*', `chart/colors/${seriesIdx}/${color}`);
    });
  });
  return chartDoc;
}

function updateApproval(chartDoc, action) {
  chartDoc = chartDoc.setIn(['chart', 'admin', 'approved', action.category], action.value);
  return updateDirty(chartDoc);
}

// Returns mutable copy of live/editable chart sizes.
function copyLiveSizes(chartDoc) {
  const sizes = {};
  const keys = Object.keys(chartDoc.chart.sizes);
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (chartDoc.chart.sizes[key].editing) {
      sizes[key] = chartDoc.chart.sizes[key].asMutable({ deep: true });
    }
  }
  return sizes;
}

function updateSpecialHorizontalBarXValue(chartDoc, action) {
  chartDoc = chartDoc.setIn(['chart', 'data', 'specialHorizontalBarXValue'], action.value);
  chartDoc = invalidate(chartDoc, '*', 'chart/data');
  chartDoc = chartDoc.setIn(['mustRender'], mustRender(chartDoc));
  return updateDirty(chartDoc);
}

function updateHorizontalBarLabel(chartDoc, action) {
  chartDoc = chartDoc.setIn(['chart', 'data', 'horizontalBarLabel'], action.value);
  chartDoc = invalidate(chartDoc, '*', 'chart/data');
  chartDoc = chartDoc.setIn(['mustRender'], mustRender(chartDoc));
  return updateDirty(chartDoc);
}

function updateShowMinorTicks(chartDoc, action) {
  chartDoc = chartDoc.setIn(['chart', 'data', 'showMinorTicks'], action.value);
  chartDoc = invalidate(chartDoc, '*', 'chart/data');
  chartDoc = chartDoc.setIn(['mustRender'], mustRender(chartDoc));
  return updateDirty(chartDoc);
}

function updateShowTooltips(chartDoc, action) {
  chartDoc = chartDoc.setIn(['chart', 'data', 'showTooltips'], action.value);
  chartDoc = invalidate(chartDoc, '*', 'chart/data');
  chartDoc = chartDoc.setIn(['mustRender'], mustRender(chartDoc));
  return updateDirty(chartDoc);
}

function updateConnectEmptyDataPoints(chartDoc, action) {
  chartDoc = chartDoc.setIn(['chart', 'data', 'connectEmptyDataPoints'], action.value);
  chartDoc = invalidate(chartDoc, '*', 'chart/data');
  chartDoc = chartDoc.setIn(['mustRender'], mustRender(chartDoc));
  return updateDirty(chartDoc);
}

function updateMarkedDataPoints(chartDoc, action) {
  chartDoc = chartDoc.setIn(['chart', 'data', 'markedDataPoints'], action.value);
  chartDoc = invalidate(chartDoc, '*', 'chart/data');
  chartDoc = chartDoc.setIn(['mustRender'], mustRender(chartDoc));
  return updateDirty(chartDoc);
}

function updateDocument(chartDoc, action) {
  switch (action.type) {
    // Data.
    case type.RECEIVE_INPUT_DATA:
      return receiveInputData(chartDoc, action);
    case type.UPDATE_DATA_FIELD:
      return updateDataField(chartDoc, action);
    case type.UPDATE_PARSED_DATA:
      return updateParsedData(chartDoc, action);
    case type.UPDATE_SERIES_NAME:
      return updateSeriesName(chartDoc, action);
    case type.UPDATE_SERIES_ORDER:
      return updateSeriesOrder(chartDoc, action);
    case type.UPDATE_SERIES_MARKDOWN:
      return updateSeriesMarkdown(chartDoc, action);
    case type.HIDE_SERIES_LEGEND:
      return hideSeriesLegend(chartDoc, action);
    case type.UPDATE_DATETIME_SUPPRESSION:
      return updateDateTimeSuppression(chartDoc, action);
    case type.UPDATE_MARKETDATA:
      return updateMarketdataProp(chartDoc, action);
    case type.SET_CHARTS_VISIBILITY:
      return setChartsVisibility(chartDoc, action);
    case type.LIST_COMPANIES:
      return listCompanies(chartDoc, action);
    case type.TOGGLE_ROLLING:
      return toggleRolling(chartDoc, action);
    case type.SINGLE_IS_PERCENT:
      return setSingleIsPercent(chartDoc, action);

    // Updates.
    case type.UPDATE_CHART_TYPE:
      return updateChartType(chartDoc, action);
    case type.UPDATE_TARGETS:
      return updateTargets(chartDoc, action);
    case type.UPDATE_SPECIAL_HORIZONTALBAR_XVALUE:
      return updateSpecialHorizontalBarXValue(chartDoc, action);
    case type.UPDATE_HORIZONTAL_BAR_LABEL:
      return updateHorizontalBarLabel(chartDoc, action);
    case type.UPDATE_SHOW_MINOR_TICKS:
      return updateShowMinorTicks(chartDoc, action);
    case type.UPDATE_SHOW_TOOLTIPS:
      return updateShowTooltips(chartDoc, action);
    case type.UPDATE_SHOW_MARKED_DATA_POINTS:
      return updateMarkedDataPoints(chartDoc, action);
    case type.UPDATE_CONNECT_EMPTY_DATA_POINTS:
      return updateConnectEmptyDataPoints(chartDoc, action);
    case type.UPDATE_GROUPS:
      return updateGroups(chartDoc, action);
    case type.PRINT_INFO:
      return updatePrintInfo(chartDoc, action);
    case type.UPDATE_SECTIONS:
      return updateSections(chartDoc, action);
    case type.UPDATE_NUMERIC_DATA_DISPLAY:
      return updateNumericDataDisplay(chartDoc, action);
    case type.UPDATE_TICK_STYLE:
      return updateAxesProp(chartDoc, 'tickStyle', action.value);
    case type.UPDATE_AXES_VISIBILITY:
      return updateAxesProp(chartDoc, 'shown', action.value);
    case type.UPDATE_AXES_PROPERTY:
      return updateAxesProp(chartDoc, action.prop, action.value);

    case type.ENABLE_MARKDOWN:
      return enableMarkdown(chartDoc, action);

    case type.UPDATE_STACKING:
      return updateStacking(chartDoc, action);
    case type.UPDATE_HORIZONTAL_BAR_TYPE:
      return updateHorizontalBarType(chartDoc, action);
    case type.UPDATE_SIZE_PROP:
      return updateChartSizeProp(chartDoc, action);
    case type.SET_WIDTH_HEIGHT_MANUALLY:
      return setWidthHeightManually(chartDoc, action);
    case type.UPDATE_GLOBAL_HEIGHT:
      return updateGlobalChartHeight(chartDoc, action);

    case type.SELECT_CHART_POINT:
      return selectChartPoint(chartDoc, action.point);
    case type.SELECT_ADJUSTED_CHART_POINT:
      return selectAdjustedChartPoint(chartDoc, action);
    case type.UPDATE_DATA_COLOR:
    case type.UPDATE_SERIES_COLOR:
    case type.UPDATE_SERIES_LINE:
      return updateColors(chartDoc, action);

    case type.UPDATE_COLOR:
      return updateColorProperty(chartDoc, action);
    case type.APPROVE_CHART:
      return updateApproval(chartDoc, action);
    default:
      return chartDoc;
  }
}

export default updateDocument;
